import { SCRIBE_REGULATION_EVENTS } from 'applications/scribe/instrumentations/track';
import cx from 'classnames';
import { PriceSurgeIcon } from 'components/v2/anytime-booking/common/price-surge-icon';
import ScribeDuck from 'ducks/scribe-duck';
import { isAreaZoningError, isAreaZoningSoftBlock } from 'helper/error-codes';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserCountryCodeFromState } from 'selectors/app-selector';
import DiscountFee from './fees/discount-fee';
import ExpandableFee from './fees/expandable-fee';
import InsuranceFee from './fees/insurance-fee';
import styles from './styles.less';
import { ErrorCircleIcon, WarningTriangleIcon } from './warning-triangle-icon';

const DeliveryQuote = ({ delivery, index }) => {
  const dispatch = useDispatch();
  const enabledCountries = ['SG'];
  const userCountryCode = useSelector(getUserCountryCodeFromState);

  const isCountryEnableGroupedServiceAndLevy = enabledCountries.includes(
    userCountryCode,
  );

  const fareDetailMatrix = _get(delivery, 'fareDetailMatrix', []);
  const serviceFeeObj =
    fareDetailMatrix &&
    fareDetailMatrix.find(fare => fare.fieldType === 'SERVICE_FEE');
  const serviceFee = serviceFeeObj ? serviceFeeObj.value : 0;
  const quote = _get(delivery, ['quote']);

  const paxExpressError = _get(delivery, 'paxExpressError');
  const hasEVRegulationError =
    paxExpressError && isAreaZoningError(paxExpressError.errorCode);

  useEffect(
    () => {
      if (hasEVRegulationError) {
        const { errorCode, title, message } = paxExpressError;
        dispatch(
          ScribeDuck.creators.scribeEVRegulationEvent({
            eventName:
              SCRIBE_REGULATION_EVENTS.EV_REGULATION_PRICE_BREAKDOWN_ERROR_VIEW,
            errorCode,
            title: message || title,
          }),
        );
      }
    },
    [hasEVRegulationError, paxExpressError, dispatch],
  );

  const InsuranceQuote = (
    <InsuranceFee
      key={`${delivery.id}-insurance`}
      index={index}
      delivery={delivery}
    />
  );

  const DiscountFeeQuote = (
    <DiscountFee key={`${delivery.id}-discount`} delivery={delivery} />
  );

  const ServiceQuote = isCountryEnableGroupedServiceAndLevy ? (
    <ExpandableFee
      fareDetailMatrix={serviceFeeObj}
      index={index}
      delivery={delivery}
      quote={quote}
    />
  ) : (
    <div key={delivery.id} className={styles.quote}>
      <span>
        {I18n.t('v2.homepage.manualOrder.heading.deliveryNo', {
          no: index + 1,
        })}
      </span>
      <span className={styles.priceTag}>
        <span> {serviceFee}</span>
        <PriceSurgeIcon
          className={styles.priceSurgeIcon}
          priceSurgeInfo={quote}
        />
      </span>
    </div>
  );

  const feesBreakdowns = [ServiceQuote];

  // Add warning text below the delivery listing for EV regulation errors
  if (hasEVRegulationError) {
    const isSoftBlock = isAreaZoningSoftBlock(paxExpressError.errorCode);
    const IconComponent = isSoftBlock ? WarningTriangleIcon : ErrorCircleIcon;
    const warningText = (
      <div key={`${delivery.id}-warning`} className={styles.quote}>
        <span className={styles.warningText}>
          <IconComponent
            className={cx(styles.warningIcon, {
              [styles.softBlockIcon]: isSoftBlock,
              [styles.hardBlockIcon]: !isSoftBlock,
            })}
          />
          {paxExpressError.title || ''}
        </span>
      </div>
    );
    feesBreakdowns.push(warningText);
  }

  feesBreakdowns.push(InsuranceQuote, DiscountFeeQuote);

  return feesBreakdowns;
};

DeliveryQuote.propTypes = {
  delivery: PropTypes.shape({}).isRequired,
};

export default DeliveryQuote;
